import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpStatusCode, HttpErrorResponse } from '@angular/common/http';

import { Observable, BehaviorSubject, throwError, iif, of, NEVER } from 'rxjs';
import { catchError, finalize, take, mergeMap, first, exhaustMap, tap } from 'rxjs/operators';
import { IdentityService } from './identity.service';
import { AuthService } from './auth.service';
import { TokenSilentRefreshService } from 'src/app/core/token-silent-refresh.service';
import { LoggerService } from 'src/app/shared/services/logger.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly _logPrefix = '[token-silent-refresh] ';
    private readonly refreshTokenInProgress$ = new BehaviorSubject<boolean>(false);
    private _isRefreshing = false;

    constructor(
        private readonly _identityService: IdentityService,
        private readonly _authService: AuthService,
        private readonly _silentTokenService: TokenSilentRefreshService,
        private readonly _loggerService: LoggerService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (this._identityService.enforceOAuth) {
            return next.handle(request);
        }
        try {
            if (!request.url.endsWith('/token')) {
                this._isRefreshing = this._silentTokenService.silentRefresh(this.refreshTokenInProgress$);
                if (this._isRefreshing) {
                    return this.refreshLogin(request, next);
                }
            }
        } catch (error: any) {
            this._loggerService.error(this._logPrefix + 'Failed to do a silent token refresh.', error);
        }

        return this._handle(request, next);
    }

    private _handle(request: HttpRequest<any>, next: HttpHandler) {
        const accessToken = this._identityService.coreAccessToken;

        if (accessToken && !(request.headers.get('Authorization')?.endsWith(accessToken) ?? false)) {
            this._loggerService.info(this._logPrefix + 'Replace the access token with a newer version');
            request = request.clone({
                setHeaders: { Authorization: 'Bearer ' + accessToken }
            });
        }

        return next.handle(request).pipe(
            catchError((error: unknown) => {
                if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
                    return this.refreshLogin(request, next);
                } else {
                    return throwError(() => error);
                }
            })
        );
    }

    private refreshLogin(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        return this.refreshTokenInProgress$.pipe(
            // Prevent changes of true/false from executing pipe more than once
            take(1),
            // When in progress, start listening for inProgress Subject changes.
            mergeMap((inProgress) =>
                iif(
                    () => inProgress,
                    this.refreshTokenInProgress$.pipe(
                        // On first emit of NOT inProgress, complete this subscription
                        first((refreshInProgress) => !refreshInProgress)
                    ),
                    of(inProgress).pipe(
                        tap(() => this.refreshTokenInProgress$.next(true)),
                        exhaustMap(() =>
                            this._authService.refreshToken().pipe(
                                catchError((e: unknown) => {
                                    this.refreshTokenInProgress$.error(e);
                                    return throwError(() => e);
                                }),
                                finalize(() => {
                                    // Update inProgress, triggering retry of other waiting 401 requests
                                    this.refreshTokenInProgress$.next(false);
                                })
                            )
                        )
                    )
                )
            ),
            // Both the completion of refresh and those waiting for in progress subject to be false land here
            mergeMap(() =>
                this._identityService.coreAccessToken$.pipe(
                    // Prevent more than one clone of the request as access token is updated
                    take(1),
                    mergeMap((newToken) => {
                        const retryRequest = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${newToken}`
                            }
                        });

                        // Retry request with new access token
                        return next.handle(retryRequest).pipe(
                            catchError((retryError: unknown) => {
                                // If the retry returns unauthorized,
                                // the server really means 'forbidden' (not authorized/insufficient permissions)
                                if (
                                    retryError instanceof HttpErrorResponse &&
                                    retryError.status === HttpStatusCode.Unauthorized
                                ) {
                                    this._authService.signOut();
                                    return NEVER;
                                } else {
                                    return throwError(() => retryError);
                                }
                            })
                        );
                    })
                )
            )
        );
    }
}
